'use client';

import { CONTENT_EVENTS } from '@repo/analytics';
import type { IMovieRatingData, IRateMovieV1Body } from '@repo/sdk';
import { trackEvent } from '@repo/web-chrome/lib/analytics';
import { Check, ChevronDown } from 'lucide-react';
import type React from 'react';
import { type RefObject, useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ITrackingStrings } from './tracking-strings';
import { WatchCountStepper } from './watch-count-stepper';

interface IWatchedControlProps {
  collectionId: number;
  data: IMovieRatingData;
  pending: boolean;
  strings: ITrackingStrings;
  save: (update: IRateMovieV1Body) => Promise<void>;
}

function useDismissDisclosure(
  open: boolean,
  rootRef: RefObject<HTMLDivElement | null>,
  close: () => void,
): void {
  useEffect(() => {
    if (!open) {
      return;
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        close();
      }
    }
    function onPointerDown(event: PointerEvent): void {
      if (
        rootRef.current &&
        event.target instanceof Node &&
        !rootRef.current.contains(event.target)
      ) {
        close();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, rootRef, close]);
}

export function WatchedControl({
  collectionId,
  data,
  pending,
  strings,
  save,
}: IWatchedControlProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const warningId = useId();

  const closeAndRefocusTrigger = useCallback((): void => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useDismissDisclosure(open, rootRef, closeAndRefocusTrigger);

  function removeFromWatched(): void {
    trackEvent(CONTENT_EVENTS.movieUnwatched, { collectionId });
    void save({ watched: false, rating: null });
    closeAndRefocusTrigger();
  }

  return (
    <div ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-primary bg-primary/10 px-3.5 text-sm font-medium text-foreground transition outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
      >
        <Check className="size-4" aria-hidden />
        {strings.watchedTimes.replace('{count}', String(data.watchCount))}
        <ChevronDown
          className={
            open ? 'size-4 rotate-180 transition-transform' : 'size-4 transition-transform'
          }
          aria-hidden
        />
      </button>
      {open && (
        <div
          id={panelId}
          className="mt-2 w-fit rounded-xl border border-border bg-card p-3 shadow-sm"
        >
          <WatchCountStepper
            collectionId={collectionId}
            count={data.watchCount}
            pending={pending}
            strings={strings}
            save={save}
          />
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            aria-describedby={warningId}
            disabled={pending}
            onClick={removeFromWatched}
            className="text-sm font-medium text-destructive transition outline-none hover:underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
          >
            {strings.removeWatched}
          </button>
          <p id={warningId} className="mt-1 text-xs text-muted-foreground">
            {strings.removeWatchedWarning}
          </p>
        </div>
      )}
    </div>
  );
}
